'use client';

import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useUser } from '@/lib/context/user-context';
import { useEpisodeHistory } from '@/hooks/useEpisodeHistory';
import { cleanAudioUrl } from '@/lib/audio-proxy-utils';
import { useUsage } from '@/contexts/usage-context';

export interface SubtitleCue {
  id: string;
  start: number;
  end: number;
  text: string;
  translation?: string | Record<string, string>;
}

interface PlayerState {
  // 当前播放内容
  currentEpisode: any | null;
  
  // 播放器状态
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackSpeed: number;
  isLoading: boolean; // 新增：加载状态
  
  // YouTube 播放器实例
  ytPlayer: any | null;
  
  // 其他状态
  cleanReadData: any | null;
  isVisible: boolean; // 控制播放控件是否显示
  subtitles: SubtitleCue[];
  activeSubtitleIndex: number | null;
  isFullPlayerOpen: boolean;
}

interface PlayerActions {
  // 设置当前播放内容
  setCurrentEpisode: (episode: any, initialTime?: number) => void;
  
  // 播放器控制
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  scrubTo: (time: number) => void;
  setPlaybackSpeed: (speed: number) => void;
  setPlaybackRate: (rate: number) => void;
  getCurrentTime: () => number;
  getPendingSeekInfo: () => { target: number; startedAt: number } | null;
  beginScrubbing: () => void;
  endScrubbing: () => void;
  handleMediaEnded: () => void;
  
  // 状态更新
  updateTime: (time: number) => void;
  updateDuration: (duration: number) => void;
  setYtPlayer: (player: any) => void;
  setCleanReadData: (data: any) => void;
  setIsLoading: (loading: boolean) => void;
  updatePlayingState: (isPlaying: boolean) => void;
  
  // 显示/隐藏控制
  showPlayer: () => void;
  hidePlayer: () => void;
  
  // 跳转控制
  skipBackward: () => void;
  skipForward: () => void;
  
  // 字幕控制
  setSubtitles: (cues: SubtitleCue[]) => void;
  clearSubtitles: () => void;
  setActiveSubtitleIndex: (index: number | null) => void;
  updateActiveSubtitleByTime: (time: number) => void;

  // 全屏播放器控制
  openFullPlayer: () => void;
  closeFullPlayer: () => void;
  toggleFullPlayer: () => void;

  // 工具函数
  formatTime: (time: number) => string;
}

interface PlaybackLockOptions {
  autoResume?: boolean;
}

interface PlaybackLockAPI {
  isPlaybackLocked: boolean;
  playbackLockReason: string | null;
  acquirePlaybackLock: (reason: string, options?: PlaybackLockOptions) => () => void;
}

type PlayerContextValue = PlayerState & PlayerActions & PlaybackLockAPI;

type MediaSessionActionDetailsLike = {
  seekTime?: number;
  seekOffset?: number;
  fastSeek?: boolean;
};

interface PlaybackLockEntry {
  id: symbol;
  reason: string;
  autoResume: boolean;
  wasPlaying: boolean;
}

// ==========================================
// 🚀 性能优化：拆分 Context
// ==========================================

/**
 * 🔥 实时状态：频繁变化（每 500ms）
 * 只有真正需要实时更新的组件才订阅这个 Context
 */
interface PlayerRealtimeState {
  currentTime: number;
  isPlaying: boolean;
  activeSubtitleIndex: number | null;
}

/**
 * 🔷 稳定状态：很少变化
 * 大部分组件应该订阅这个
 */
interface PlayerStableState {
  currentEpisode: any | null;
  duration: number;
  playbackSpeed: number;
  isLoading: boolean;
  ytPlayer: any | null;
  cleanReadData: any | null;
  isVisible: boolean;
  subtitles: SubtitleCue[];
  isFullPlayerOpen: boolean;
}

// 创建三个独立的 Context
const PlayerRealtimeContext = createContext<PlayerRealtimeState | null>(null);
const PlayerStableContext = createContext<PlayerStableState | null>(null);
const PlayerActionsContext = createContext<(PlayerActions & PlaybackLockAPI) | null>(null);

// 旧的 Context（保留用于向后兼容）
const PlayerContext = createContext<PlayerContextValue | null>(null);

const PLAYER_DEBUG_ENABLED = typeof process !== 'undefined'
  ? process.env.NODE_ENV !== 'production'
  : true;

const playerDebugLog = (...args: unknown[]) => {
  if (!PLAYER_DEBUG_ENABLED) return;
  try {
    // console.log('[PlayerContext]', ...args);
  } catch {
    // ignore logging errors
  }
};

// ==========================================
// 🪝 新的细粒度 Hooks
// ==========================================

/**
 * 🔥 订阅实时状态（currentTime, isPlaying）
 * ⚠️ 警告：这个 hook 会导致组件每 500ms 重渲染一次
 * 只在真正需要实时更新的组件中使用（如时间显示、进度条）
 * 
 * @example
 * ```tsx
 * function TimeDisplay() {
 *   const { currentTime } = usePlayerRealtime(); // 🔥 500ms 更新
 *   const { formatTime } = usePlayerActions();
 *   return <span>{formatTime(currentTime)}</span>;
 * }
 * ```
 */
export const usePlayerRealtime = (): PlayerRealtimeState => {
  const context = useContext(PlayerRealtimeContext);
  if (!context) {
    throw new Error('usePlayerRealtime must be used within PlayerProvider');
  }
  return context;
};

/**
 * 🔷 订阅稳定状态（episode, duration, subtitles 等）
 * ✅ 推荐：大部分组件应该使用这个
 * 
 * @example
 * ```tsx
 * function EpisodeInfo() {
 *   const { currentEpisode, duration } = usePlayerStable(); // 🔷 很少更新
 *   return <div>{currentEpisode?.title}</div>;
 * }
 * ```
 */
export const usePlayerStable = (): PlayerStableState => {
  const context = useContext(PlayerStableContext);
  if (!context) {
    throw new Error('usePlayerStable must be used within PlayerProvider');
  }
  return context;
};

/**
 * ⚡ 订阅 Actions（play, pause, seekTo 等）
 * ✅ 最安全：这些函数永远不变，不会导致重渲染
 * 
 * @example
 * ```tsx
 * function PlayButton() {
 *   const { play, pause } = usePlayerActions(); // ⚡ 永不重渲染
 *   const { isPlaying } = usePlayerStable();
 *   return <button onClick={isPlaying ? pause : play}>Play/Pause</button>;
 * }
 * ```
 */
export const usePlayerActions = (): PlayerActions & PlaybackLockAPI => {
  const context = useContext(PlayerActionsContext);
  if (!context) {
    throw new Error('usePlayerActions must be used within PlayerProvider');
  }
  return context;
};

/**
 * 🔄 完整的 Player Hook（向后兼容）
 * ⚠️ 已废弃：请使用细粒度的 hooks (usePlayerRealtime, usePlayerStable, usePlayerActions)
 * 
 * @deprecated 使用 usePlayerRealtime, usePlayerStable, usePlayerActions 代替
 * 
 * @example
 * ```tsx
 * // ❌ 旧用法：整个组件会每 500ms 重渲染
 * const player = usePlayer();
 * 
 * // ✅ 新用法：只订阅需要的部分
 * const actions = usePlayerActions();
 * const stable = usePlayerStable();
 * ```
 */
export const usePlayer = (): PlayerContextValue => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

// 持久化存储的键名
const PLAYER_STORAGE_KEY = 'neofolio-player-state';
const FREE_PREVIEW_MAX_SECONDS = 120;
export const FREE_PREVIEW_LOCK_REASON = 'Free plan preview is limited to the first 2 minutes. Upgrade to continue listening.';
const SEEK_SETTLE_TOLERANCE = 0.35;
const SEEK_GUARD_WINDOW_MS = 700;

// 从 localStorage 恢复状态的辅助函数（独立函数，不依赖组件状态）
const extractEpisodeId = (episode: any | null | undefined): string | null => {
  if (!episode || typeof episode !== 'object') {
    return null;
  }

  return (
    episode.shareId ??
    episode.content?.shareId ??
    episode.content?.content?.id ??
    null
  );
};

const loadInitialPlayerState = (): Partial<PlayerState> | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    const saved = localStorage.getItem(PLAYER_STORAGE_KEY);
    if (!saved) return null;
    
    const parsedState = JSON.parse(saved) as Partial<PlayerState> & {
      activeEpisodeId?: string | null;
      timestamp?: number;
    };
    const savedEpisodeId = parsedState.activeEpisodeId ?? extractEpisodeId(parsedState.currentEpisode);
    const pathname = window.location?.pathname ?? '';
    const routeEpisodeMatch = pathname.match(/^\/episodes\/([^/]+)/);
    const currentRouteEpisodeId = routeEpisodeMatch?.[1] ?? null;
    
    // 检查状态是否过期（24小时）
    const timestamp = typeof parsedState.timestamp === 'number' ? parsedState.timestamp : null;
    const isExpired = timestamp !== null
      ? Date.now() - timestamp > 24 * 60 * 60 * 1000
      : true;
    if (isExpired) {
      localStorage.removeItem(PLAYER_STORAGE_KEY);
      return null;
    }

    if (currentRouteEpisodeId && savedEpisodeId && currentRouteEpisodeId !== savedEpisodeId) {
      return null;
    }

    // 移除只用于比较的辅助字段
    delete parsedState.activeEpisodeId;
    
    return parsedState;
  } catch (error) {
    console.error('Failed to load player state on init:', error);
    return null;
  }
};

const getCueEnd = (cues: SubtitleCue[], index: number): number => {
  const cue = cues[index];
  if (!cue) return 0;
  const explicitEnd = Number(cue.end);
  const start = Number(cue.start) || 0;
  if (Number.isFinite(explicitEnd) && explicitEnd > start) {
    return explicitEnd;
  }
  const nextCue = cues[index + 1];
  if (nextCue) {
    const nextStart = Number(nextCue.start) || start;
    if (Number.isFinite(nextStart) && nextStart > start) {
      return Math.max(nextStart - 0.01, start + 0.1);
    }
  }
  return start + 5;
};

const findSubtitleIndexForTime = (cues: SubtitleCue[], time: number): number | null => {
  if (!cues.length || !Number.isFinite(time) || time < 0) {
    return null;
  }

  let low = 0;
  let high = cues.length - 1;
  let candidate: number | null = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const cue = cues[mid];
    const cueStart = Number(cue.start) || 0;
    const cueEnd = getCueEnd(cues, mid);

    if (time >= cueStart && time <= cueEnd + 0.05) {
      candidate = mid;
      break;
    }

    if (time < cueStart) {
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  if (candidate !== null) {
    return candidate;
  }

  if (low <= 0) {
    return null;
  }

  const previousIndex = Math.min(low, cues.length) - 1;
  const previousCue = cues[previousIndex];
  if (!previousCue) {
    return null;
  }

  if (time >= previousCue.start) {
    return previousIndex;
  }

  return null;
};

export const PlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get user for history tracking
  const { user } = useUser();
  const { usageInfo, loading: usageLoading } = useUsage();
  const userRef = useRef(user);
  userRef.current = user; // 始终保持最新的user引用

  const hasTwoBrokeGirlsAccess = useMemo(() => {
    const flag = user?.user_metadata?.twoBrokeGirlsAccess === true;
    if (!flag) return false;

    const expiresAt = user?.user_metadata?.twoBrokeGirlsAccessExpiresAt;
    if (!expiresAt) return true;

    const expiresDate = new Date(expiresAt);
    if (Number.isNaN(expiresDate.getTime())) {
      return true;
    }

    return expiresDate > new Date();
  }, [user?.user_metadata?.twoBrokeGirlsAccess, user?.user_metadata?.twoBrokeGirlsAccessExpiresAt]);

  const isPremiumUser = useMemo(
    () => {
      const planType = usageInfo?.planType?.toLowerCase();
      return (
        usageInfo?.isPremium === true ||
        planType === 'premium' ||
        user?.user_metadata?.isPremium === true
      );
    },
    [usageInfo?.isPremium, usageInfo?.planType, user?.user_metadata?.isPremium]
  );
  // 🔥 检查是否为 Basic Plan 或 Basic Plan Lite 用户
  // 注意：planType 可能是 'basic' 或 'basic_lite'，两者都是付费计划，不应触发免费用户的2分钟限制
  const isBasicPlanLiteUser = useMemo(
    () => {
      const planType = usageInfo?.planType?.toLowerCase();
      return (
        usageInfo?.isBasicPlanLite === true ||
        planType === 'basic_lite' ||
        planType === 'basic' ||  // 🔥 新增：Basic Plan 用户也应该被识别为付费用户
        user?.user_metadata?.isBasicPlanLite === true ||
        user?.user_metadata?.isBasic === true  // 🔥 新增：检查 isBasic 标识
      );
    },
    [usageInfo?.isBasicPlanLite, usageInfo?.planType, user?.user_metadata?.isBasicPlanLite, user?.user_metadata?.isBasic]
  );
  const isAdminUser = useMemo(
    () => user?.user_metadata?.isAdmin === true,
    [user?.user_metadata?.isAdmin]
  );
  const isFreePlanUser = useMemo(
    () => {
      // 🔥 修复：在 usageInfo 加载期间，采用保守策略，默认不判定为免费用户
      // 避免在加载期间误触发 2 分钟限制（特别是 YouTube 视频加载快的情况）
      if (usageLoading) {
        // 如果还在加载中，检查 user_metadata 作为快速判断
        // 🔥 包含所有付费计划：Premium, Basic, Basic Lite
        if (
          user?.user_metadata?.isPremium || 
          user?.user_metadata?.isBasicPlanLite || 
          user?.user_metadata?.isBasic ||  // 🔥 新增：Basic Plan 用户
          hasTwoBrokeGirlsAccess
        ) {
          return false;
        }
        // 如果没有 metadata 且还在加载，保守地不判定为免费用户
        // 等加载完成后再做最终判断
        return false;
      }

      // 加载完成后，根据实际数据判断
      return !isPremiumUser && !isBasicPlanLiteUser && !isAdminUser && !hasTwoBrokeGirlsAccess;
    },
    [isPremiumUser, isBasicPlanLiteUser, isAdminUser, hasTwoBrokeGirlsAccess, usageLoading, user?.user_metadata?.isPremium, user?.user_metadata?.isBasicPlanLite, user?.user_metadata?.isBasic, hasTwoBrokeGirlsAccess]
  );
  
  
  // 修改状态初始化逻辑，支持从 localStorage 恢复
  const [state, setState] = useState<PlayerState>(() => {
    // 在客户端尝试恢复状态
    if (typeof window !== 'undefined') {
      const savedState = loadInitialPlayerState();
      
      if (savedState) {
        return {
          currentEpisode: savedState.currentEpisode || null,
          isPlaying: false, // 刷新后不自动播放
          currentTime: savedState.currentTime || 0,
          duration: savedState.duration || 0,
          playbackSpeed: savedState.playbackSpeed || 1,
          isLoading: false, // 初始不加载
          ytPlayer: null,
          cleanReadData: null,
          // 只要有currentEpisode就设置为visible，文本内容由组件处理hydration
          isVisible: savedState.currentEpisode ? true : false,
          subtitles: [],
          activeSubtitleIndex: null,
          isFullPlayerOpen: false,
        };
      }
    }
    
    // 默认初始状态
    return {
      currentEpisode: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      playbackSpeed: 1,
      isLoading: false,
      ytPlayer: null,
      cleanReadData: null,
      isVisible: false,
      subtitles: [],
      activeSubtitleIndex: null,
      isFullPlayerOpen: false,
    };
  });

  const [playbackLockState, setPlaybackLockState] = useState<{ isLocked: boolean; reason: string | null }>({
    isLocked: false,
    reason: null,
  });
  const playbackLockStackRef = useRef<PlaybackLockEntry[]>([]);
  const previewLimitActiveRef = useRef(false);
  const isFreePlanUserRef = useRef(isFreePlanUser);
  const pendingPlayRequestRef = useRef(false);

  // 保存状态到 localStorage 的函数
  const savePlayerState = useCallback((playerState: Partial<PlayerState>) => {
    if (typeof window === 'undefined') return;
    
    try {
      const stateToSave = {
        currentEpisode: playerState.currentEpisode,
        currentTime: playerState.currentTime || 0,
        duration: playerState.duration || 0,
        playbackSpeed: playerState.playbackSpeed || 1,
        activeEpisodeId: extractEpisodeId(playerState.currentEpisode) || null,
        isVisible: playerState.isVisible || false,
        // 只保存必要的数据，不保存播放器实例
        timestamp: Date.now() // 添加时间戳，避免过期状态
      };
      
      localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Failed to save player state:', error);
    }
  }, []);

  // 清除保存的状态
  const clearPlayerState = useCallback(() => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(PLAYER_STORAGE_KEY);
  }, []);

  useEffect(() => {
    isFreePlanUserRef.current = isFreePlanUser;
    if (!isFreePlanUser && previewLimitActiveRef.current && playbackLockStackRef.current.length === 0) {
      previewLimitActiveRef.current = false;
      setPlaybackLockState(prev => {
        if (prev.reason === FREE_PREVIEW_LOCK_REASON) {
          return { isLocked: false, reason: null };
        }
        return prev;
      });
    }
  }, [isFreePlanUser]);


  const timeUpdateInterval = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const subtitlesRef = useRef<SubtitleCue[]>(state.subtitles || []);
  const activeSubtitleIndexRef = useRef<number | null>(state.activeSubtitleIndex || null);
  const mediaSessionConfiguredRef = useRef(false);
  const playRef = useRef<() => void | Promise<void>>(() => {});
  const pauseRef = useRef<() => void>(() => {});
  const skipBackwardRef = useRef<() => void>(() => {});
  const skipForwardRef = useRef<() => void>(() => {});
  const seekToRef = useRef<(time: number) => void>(() => {});
 
  const lastAcceptedTimeRef = useRef<{ time: number; timestamp: number }>({
    time: 0,
    timestamp: 0,
  });
  const MIN_TIME_UPDATE_INTERVAL_MS = 120;
  const MIN_TIME_UPDATE_DELTA = 0.08;
  const shouldThrottleTimeUpdate = useCallback((nextTime: number) => {
    const now =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    const last = lastAcceptedTimeRef.current;

    if (
      Math.abs(nextTime - last.time) < MIN_TIME_UPDATE_DELTA &&
      now - last.timestamp < MIN_TIME_UPDATE_INTERVAL_MS
    ) {
      return true;
    }

    lastAcceptedTimeRef.current = { time: nextTime, timestamp: now };
    return false;
  }, []);
  
  // 使用 ref 来访问最新的状态值（提前定义以供其他函数使用）
  const stateRef = useRef(state);
  stateRef.current = state; // 始终保持最新的state引用
  subtitlesRef.current = state.subtitles || [];
  activeSubtitleIndexRef.current = state.activeSubtitleIndex ?? null;

  const pathname = usePathname();

  // Get thumbnail URL with multiple fallback options
  const getThumbnailUrl = useCallback(() => {
    if (!state.currentEpisode) return undefined;
    
    const episode = state.currentEpisode.content || state.currentEpisode;
    
    // Try different thumbnail fields in order of preference
    const thumbnailUrl = episode.thumbnail || 
                        episode.videoThumbnail || 
                        episode.ownerAvatarUrl ||
                        episode.avatar ||
                        undefined;
    
    return thumbnailUrl;
  }, [state.currentEpisode]);

  const updateMediaSessionMetadata = useCallback(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return;
    }

    const episode = stateRef.current.currentEpisode;
    if (!episode || typeof window === 'undefined' || !('MediaMetadata' in window)) {
      return;
    }

    const content = episode.content || episode;
    const title = content.title || content.name || 'Now Playing';
    const artist = content.author || content.podcastTitle || content.channelTitle || 'Podcast';
    const album = content.podcastTitle || content.seriesTitle || artist;
    const artworkUrl = getThumbnailUrl();

    try {
      const metadataInit: MediaMetadataInit = {
        title,
        artist,
        album,
      };

      if (artworkUrl) {
        metadataInit.artwork = [
          {
            src: artworkUrl,
            sizes: '512x512',
            type: 'image/png',
          },
        ];
      }

      navigator.mediaSession!.metadata = new (window as any).MediaMetadata(metadataInit);
    } catch (error) {
      console.error('Failed to update media session metadata', error);
    }
  }, [getThumbnailUrl]);

  const updateMediaSessionPlaybackState = useCallback((playbackState: 'none' | 'paused' | 'playing') => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return;
    }

    try {
      navigator.mediaSession!.playbackState = playbackState;
    } catch (error) {
      console.error('Failed to update media session playbackState', error);
    }
  }, []);

  // 🔥 节流 MediaSession 位置更新，避免频繁调用浏览器 API
  const lastMediaSessionUpdateRef = useRef(0);
  const MEDIA_SESSION_UPDATE_THROTTLE = 1000; // 1 秒更新一次足够

  const updateMediaSessionPositionState = useCallback(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return;
    }

    const audio = audioPlayerRef.current;
    if (!audio || typeof navigator.mediaSession!.setPositionState !== 'function') {
      return;
    }

    if (!isFinite(audio.duration) || audio.duration <= 0) {
      return;
    }

    // 🔥 节流：减少 MediaSession API 调用频率
    const now = Date.now();
    if (now - lastMediaSessionUpdateRef.current < MEDIA_SESSION_UPDATE_THROTTLE) {
      return;
    }
    lastMediaSessionUpdateRef.current = now;

    try {
      navigator.mediaSession!.setPositionState({
        duration: audio.duration,
        position: audio.currentTime,
        playbackRate: audio.playbackRate || 1,
      });
    } catch (error) {
      console.error('Failed to update media session position', error);
    }
  }, []);

  const configureMediaSessionActions = useCallback(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return;
    }

    if (mediaSessionConfiguredRef.current) {
      return;
    }

    const safeSetAction = (
      action: any,
      handler: any
    ) => {
      try {
        navigator.mediaSession!.setActionHandler(action, handler);
      } catch (error) {
        console.error(`Failed to set media session handler for ${action}`, error);
      }
    };

    safeSetAction('play', async () => {
      await playRef.current?.();
    });

    safeSetAction('pause', () => {
      pauseRef.current?.();
    });

    safeSetAction('seekbackward', (details: MediaSessionActionDetailsLike | undefined) => {
      const offset = details?.seekOffset ?? 15;
      const base = audioPlayerRef.current?.currentTime ?? stateRef.current.currentTime;
      seekToRef.current(Math.max(0, base - offset));
    });

    safeSetAction('seekforward', (details: MediaSessionActionDetailsLike | undefined) => {
      const offset = details?.seekOffset ?? 15;
      const base = audioPlayerRef.current?.currentTime ?? stateRef.current.currentTime;
      const duration = audioPlayerRef.current?.duration ?? stateRef.current.duration;
      const max = Number.isFinite(duration) && duration > 0 ? duration : base + offset;
      seekToRef.current(Math.min(base + offset, max));
    });

    safeSetAction('seekto', (details: MediaSessionActionDetailsLike | undefined) => {
      if (typeof details?.seekTime === 'number') {
        seekToRef.current(details.seekTime);
      }
    });

    safeSetAction('stop', () => {
      pauseRef.current?.();
    });

    safeSetAction('previoustrack', () => {
      skipBackwardRef.current?.();
    });

    safeSetAction('nexttrack', () => {
      skipForwardRef.current?.();
    });

    mediaSessionConfiguredRef.current = true;
  }, []);

  const clearMediaSession = useCallback(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return;
    }

    try {
      navigator.mediaSession!.metadata = null;
      navigator.mediaSession!.playbackState = 'none';
    } catch (error) {
      console.error('Failed to clear media session', error);
    }

    mediaSessionConfiguredRef.current = false;
  }, []);

  // Initialize episode history tracking with dynamic episode info
  // 使用实际的episode数据，而不是初始化时的空值
  const currentEpisodeId = state.currentEpisode?.shareId || state.currentEpisode?.content?.shareId || '';
  const currentEpisodeTitle = state.currentEpisode?.content?.title || state.currentEpisode?.title || '';
  const currentPodcastTitle = state.currentEpisode?.content?.author || state.currentEpisode?.content?.podcastTitle || 'Unknown Podcast';
  const currentOwnerAvatarUrl = state.currentEpisode?.content?.ownerAvatarUrl || state.currentEpisode?.ownerAvatarUrl;
  
  const { trackProgress, saveProgressImmediately, markCompleted } = useEpisodeHistory(
    currentEpisodeId,
    currentEpisodeTitle,
    currentPodcastTitle,
    getThumbnailUrl(),
    state.currentEpisode?.content?.description || state.currentEpisode?.description || '',
    currentOwnerAvatarUrl,
    state.duration
  );

  // 🎯 Podverse风格：页面关闭前保存进度（放在 saveProgressImmediately 定义之后）
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (audioPlayerRef.current && audioPlayerRef.current.currentTime > 0) {
        const currentTime = audioPlayerRef.current.currentTime;
        const duration = audioPlayerRef.current.duration;
        if (duration) {
          saveProgressImmediately(currentTime, duration, 'page_unload');
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && audioPlayerRef.current && audioPlayerRef.current.currentTime > 0) {
        const currentTime = audioPlayerRef.current.currentTime;
        const duration = audioPlayerRef.current.duration;
        if (duration) {
          saveProgressImmediately(currentTime, duration, 'page_hidden');
        }
      }
    };

    // 🎯 监听路由变化（包括 bottom player 点击跳转）
    const handleRouteChange = () => {
      if (audioPlayerRef.current && audioPlayerRef.current.currentTime > 0) {
        const currentTime = audioPlayerRef.current.currentTime;
        const duration = audioPlayerRef.current.duration;
        if (duration) {
          saveProgressImmediately(currentTime, duration, 'route_change');
        }
      }
    };

    // 监听 popstate 事件（浏览器前进后退）
    window.addEventListener('popstate', handleRouteChange);
    
    // 监听点击事件，检测导航链接点击
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href && audioPlayerRef.current && audioPlayerRef.current.currentTime > 0) {
        // 检查是否是内部导航
        const isInternalLink = link.href.startsWith(window.location.origin);
        if (isInternalLink) {
          const currentTime = audioPlayerRef.current.currentTime;
          const duration = audioPlayerRef.current.duration;
          if (duration) {
              saveProgressImmediately(currentTime, duration, 'navigation_click');
          }
        }
      }
    };

    // 🎯 监听底部播放器导航事件
    const handleSaveBeforeNavigation = (event: CustomEvent) => {
      const { currentTime, duration, reason } = event.detail;
      if (currentTime > 0 && duration) {
        saveProgressImmediately(currentTime, duration, reason);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('click', handleClick);
    window.addEventListener('saveProgressBeforeNavigation', handleSaveBeforeNavigation as EventListener);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handleRouteChange);
      document.removeEventListener('click', handleClick);
      window.removeEventListener('saveProgressBeforeNavigation', handleSaveBeforeNavigation as EventListener);
    };
  }, [saveProgressImmediately, updateMediaSessionPlaybackState, updateMediaSessionPositionState]);

  // 格式化时间
  const formatTime = useCallback((time: number): string => {
    // 处理无效值
    if (!time || isNaN(time) || time < 0) {
      return '0:00';
    }
    
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const commitActiveSubtitleIndex = useCallback((index: number | null) => {
    activeSubtitleIndexRef.current = index;
    setState(prev => {
      if (prev.activeSubtitleIndex === index) {
        return prev;
      }
      return { ...prev, activeSubtitleIndex: index };
    });
  }, []);

  const setSubtitles = useCallback((cues: SubtitleCue[]) => {
    const sanitized = Array.isArray(cues)
      ? cues
          .filter((cue): cue is SubtitleCue => Boolean(cue) && typeof cue === 'object')
          .map((cue, index) => {
            const start = Number(cue.start);
            const end = Number(cue.end);
            return {
              id: cue.id || `subtitle-${index}-${Math.round((Number.isFinite(start) ? start : 0) * 1000)}`,
              start: Number.isFinite(start) ? start : 0,
              end: Number.isFinite(end) ? end : NaN,
              text: typeof cue.text === 'string' ? cue.text : '',
              translation: cue.translation,
            } satisfies SubtitleCue;
          })
          .filter((cue) => cue.text.trim().length > 0)
      : [];

    sanitized.sort((a, b) => a.start - b.start);

    subtitlesRef.current = sanitized;

    setState(prev => {
      const prevSubtitles = prev.subtitles || [];
      const isSameLength = prevSubtitles.length === sanitized.length;
      const isSame = isSameLength && prevSubtitles.every((prevCue, idx) => {
        const nextCue = sanitized[idx];
        if (!nextCue) return false;
        return (
          prevCue.id === nextCue.id &&
          prevCue.start === nextCue.start &&
          prevCue.end === nextCue.end &&
          prevCue.text === nextCue.text &&
          prevCue.translation === nextCue.translation
        );
      });

      if (isSame) {
        return prev;
      }

      return { ...prev, subtitles: sanitized };
    });

    if (sanitized.length === 0) {
      commitActiveSubtitleIndex(null);
      return;
    }

    const currentTime = stateRef.current.currentTime;
    const nextIndex = findSubtitleIndexForTime(sanitized, currentTime);
    commitActiveSubtitleIndex(nextIndex);
  }, [commitActiveSubtitleIndex]);

  const clearSubtitles = useCallback(() => {
    subtitlesRef.current = [];
    setState(prev => {
      if (prev.subtitles.length === 0 && prev.activeSubtitleIndex === null) {
        return prev;
      }
      return { ...prev, subtitles: [], activeSubtitleIndex: null };
    });
    activeSubtitleIndexRef.current = null;
  }, []);

  const updateActiveSubtitleByTime = useCallback((time: number) => {
    const cues = subtitlesRef.current;
    if (!cues.length) {
      if (activeSubtitleIndexRef.current !== null) {
        commitActiveSubtitleIndex(null);
      }
      return;
    }

    const nextIndex = findSubtitleIndexForTime(cues, time);
    if (nextIndex !== activeSubtitleIndexRef.current) {
      commitActiveSubtitleIndex(nextIndex);
    }
  }, [commitActiveSubtitleIndex]);

  const setActiveSubtitleIndex = useCallback((index: number | null) => {
    commitActiveSubtitleIndex(index);
  }, [commitActiveSubtitleIndex]);

  const openFullPlayer = useCallback(() => {
    setState(prev => (prev.isFullPlayerOpen ? prev : { ...prev, isFullPlayerOpen: true }));
  }, []);

  const closeFullPlayer = useCallback(() => {
    setState(prev => (prev.isFullPlayerOpen ? { ...prev, isFullPlayerOpen: false } : prev));
  }, []);

  const toggleFullPlayer = useCallback(() => {
    setState(prev => ({ ...prev, isFullPlayerOpen: !prev.isFullPlayerOpen }));
  }, []);

  // 使用 ref 来跟踪是否正在等待缓冲，避免重复触发
  const isWaitingForBufferRef = useRef(false);
  
  // 🔋 优化：HTML5 音频时间更新节流
  const lastAudioTimeUpdateRef = useRef(0);
  // 🔥 性能优化：增加节流时间从 200ms 到 500ms，减少 CPU 负载和组件重渲染
  const AUDIO_TIME_UPDATE_THROTTLE_MS = 500; // 每 500ms 最多更新一次
  
  const handleAudioTimeUpdate = useCallback(() => {
    if (audioPlayerRef.current) {
      const audio = audioPlayerRef.current;
      const currentTime = audio.currentTime;
      const duration = audio.duration;
      
      // 🔋 节流：限制更新频率以减少 CPU 负载
      const now = Date.now();
      if (now - lastAudioTimeUpdateRef.current < AUDIO_TIME_UPDATE_THROTTLE_MS) {
        return; // 跳过此次更新
      }
      lastAudioTimeUpdateRef.current = now;
      
      // 重新获取最新的episode信息，避免闭包问题
      const latestEpisodeId = stateRef.current.currentEpisode?.shareId || stateRef.current.currentEpisode?.content?.shareId || '';
      const latestEpisodeTitle = stateRef.current.currentEpisode?.content?.title || stateRef.current.currentEpisode?.title || '';
      
      // 🔥 暂时禁用自动缓冲暂停功能，让浏览器自己处理缓冲
      // 浏览器的内置缓冲处理通常更可靠
      // 如果需要，可以只记录缓冲状态而不干预播放
      if (false && stateRef.current.isPlaying && !audio.paused) {
        // 功能暂时禁用
      }
      
      // 🎯 Podverse风格：禁用持续的progress tracking
      // Track progress for history - DISABLED for Podverse-style approach
      // if (currentTime > 0 && duration && userRef.current && stateRef.current.currentEpisode) {
      //   trackProgress(currentTime, duration);
      // }
      
      // 保留调试日志但降低频率
      if (Math.floor(currentTime) % 15 === 0) { // 每15秒记录一次状态
      }
      
      // 只在时间真正变化时更新，避免不必要的状态更新
      setState(prev => {
        if (Math.abs(prev.currentTime - currentTime) > 0.1) {
          return { ...prev, currentTime };
        }
        return prev;
      });

      updateActiveSubtitleByTime(currentTime);
      updateMediaSessionPositionState();
    }
  }, [trackProgress, updateActiveSubtitleByTime, updateMediaSessionPositionState]); // 移除user依赖，因为使用userRef

  const handleAudioEnded = useCallback(() => {
    const fallbackDuration = stateRef.current.duration;
    const audioDuration = audioPlayerRef.current?.duration;
    const finishedAt = (typeof audioDuration === 'number' && Number.isFinite(audioDuration) && audioDuration > 0)
      ? audioDuration
      : fallbackDuration;

    setState(prev => ({ ...prev, isPlaying: false, currentTime: finishedAt }));
    updateMediaSessionPlaybackState('paused');
    updateMediaSessionPositionState();

    if (finishedAt > 0) {
      markCompleted(finishedAt, audioDuration || fallbackDuration);
    }
  }, [updateMediaSessionPlaybackState, updateMediaSessionPositionState, markCompleted]);

  const handleAudioLoaded = useCallback(() => {
    if (audioPlayerRef.current) {
      const duration = audioPlayerRef.current.duration;
      setState(prev => ({ ...prev, duration, isLoading: false }));
      updateMediaSessionMetadata();
      updateMediaSessionPlaybackState(stateRef.current.isPlaying ? 'playing' : 'paused');
      updateMediaSessionPositionState();
    }
  }, [updateMediaSessionMetadata, updateMediaSessionPlaybackState, updateMediaSessionPositionState]);

  // 辅助函数：检测是否为 blob 视频（即使 origin 未标注为 video 也识别）
  const isEpisodeBlobVideo = useCallback((episode: any): boolean => {
    if (!episode) return false;
    const videoUrl = episode?.content?.videoUrl || episode?.videoUrl || '';
    return typeof videoUrl === 'string' && videoUrl.includes('blob.vercel-storage.com');
  }, []);

  // 初始化音频播放器
  const initializeAudioPlayer = useCallback((episode: any, initialTime?: number) => {
    if (typeof window === 'undefined') return;
    
    // 检查是否为YouTube视频内容
    const origin = episode?.content?.origin || episode?.origin;
    const rawAudioUrl = episode?.audioUrl || episode?.content?.audioUrl || episode?.content?.videoUrl || episode?.videoUrl;
    
    // 检查原始URL来判断是否是YouTube内容
    const originalUrl = episode?.content?.videoUrl || episode?.videoUrl;
    const isYouTubeContent = originalUrl && (originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be'));

    // 🔥 关键修复：识别 blob 视频（不再强依赖 origin 字段）
    const isBlobVideo = isEpisodeBlobVideo(episode);

    const isVideoLikeContent = origin === 'video' || isBlobVideo;
    const shouldUseEmbeddedVideo = isVideoLikeContent;

    // 在 episodes 页面让嵌入式视频播放器处理音频；在其他页面为非 YouTube 视频准备音频后备
    if (shouldUseEmbeddedVideo) {
      // console.log('🔇 Skipping audio player creation for embedded video handling:', { origin, isYouTubeContent, isBlobVideo });
      clearMediaSession();
      return;
    }

    // YouTube 内容始终由 iframe 播放器处理（包括全局 fallback），不创建 HTML5 音频
    if (isYouTubeContent) {
      clearMediaSession();
      return;
    }

    
    // 如果没有音频URL，也不创建播放器
    if (!rawAudioUrl) {
      clearMediaSession();
      return;
    }
    
    // 🔥 统一音频代理解决方案 - 现在使用共享的音频代理工具
    
    const audioUrl = cleanAudioUrl(rawAudioUrl);
    
    // 检查是否是相同的音频URL
    const isSameUrl = audioPlayerRef.current && audioPlayerRef.current.src === audioUrl;
    
    if (isSameUrl) {
      // 如果是相同的URL，只更新时间（如果需要）
      if (initialTime !== undefined && initialTime > 0 && audioPlayerRef.current) {
        audioPlayerRef.current.currentTime = initialTime;
        setState(prev => ({ ...prev, currentTime: initialTime }));
      }
      updateMediaSessionMetadata();
      updateMediaSessionPositionState();
      configureMediaSessionActions();
      return; // 不重新创建音频元素
    }
    
    // 保存当前播放状态和时间
    const wasPlaying = audioPlayerRef.current && !audioPlayerRef.current.paused;
    const currentAudioTime = audioPlayerRef.current ? audioPlayerRef.current.currentTime : 0;
    
    // 清理现有的音频播放器
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.removeEventListener('timeupdate', handleAudioTimeUpdate);
      audioPlayerRef.current.removeEventListener('ended', handleAudioEnded);
      audioPlayerRef.current.removeEventListener('loadedmetadata', handleAudioLoaded);
    }
    
    // 创建新的音频元素
    const audio = new Audio();
    audio.src = audioUrl;
    audio.preload = 'metadata';
    
    // 尝试添加无广告标识（如果支持的话）
    // 注意：这只是尝试，某些CDN可能忽略这些头部
    audio.crossOrigin = 'anonymous';
    
    // 添加事件监听器
    audio.addEventListener('timeupdate', handleAudioTimeUpdate);
    audio.addEventListener('ended', handleAudioEnded);
    
    // 特殊处理loadedmetadata事件，用于设置初始时间
    const handleLoadedMetadata = () => {
      const duration = audio.duration;
      
      // 先更新duration并清除加载状态
      setState(prev => ({ ...prev, duration, isLoading: false }));
      
      // 只有在真正需要设置初始时间时才设置（防止暂停/恢复时重置时间）
      if (initialTime !== undefined && initialTime > 0 && initialTime < duration) {
        audio.currentTime = initialTime;
        // 立即更新状态以保持同步
        setState(prev => ({ ...prev, currentTime: initialTime }));
      } else {
        // 检查当前状态中是否有时间，如果有则应用到音频元素
        const currentStateTime = stateRef.current.currentTime;
        if (currentStateTime > 0 && currentStateTime < duration) {
          audio.currentTime = currentStateTime;
        }
      }
    };
    
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('loadedmetadata', handleAudioLoaded);
    
    audioPlayerRef.current = audio;
    configureMediaSessionActions();
  }, [
    handleAudioTimeUpdate,
    handleAudioEnded,
    handleAudioLoaded,
    clearMediaSession,
    updateMediaSessionMetadata,
    updateMediaSessionPositionState,
    configureMediaSessionActions,
  ]);

  // 检查是否为已知的临时ID到永久ID转换
  const checkIdConversion = useCallback((newEpisodeId: string, currentEpisodeId: string): boolean => {
    if (typeof window === 'undefined') return false;
    
    try {
      const mappings = JSON.parse(localStorage.getItem('tempIdMappings') || '{}');
      
      // 检查是否为已知的ID转换：
      // 1. 当前ID是临时ID (temp-开头)
      // 2. 新ID是永久ID且在映射中存在
      const isCurrentTempId = currentEpisodeId?.startsWith('temp-');
      const isNewIdInMappings = Object.values(mappings).includes(newEpisodeId);
      
      if (isCurrentTempId && isNewIdInMappings) {
        // 进一步验证：检查临时ID是否映射到这个永久ID
        const tempIdEntry = Object.entries(mappings).find(([tempKey, permanentId]) => {
          return permanentId === newEpisodeId && tempKey.includes(currentEpisodeId.split('-')[1]);
        });
        
        if (tempIdEntry) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking ID conversion:', error);
      return false;
    }
  }, []);

  // 设置当前播放内容
  const setCurrentEpisode = useCallback((episode: any, initialTime?: number) => {
    const episodeId = episode?.shareId || episode?.content?.shareId;
    const currentEpisodeId = stateRef.current.currentEpisode?.shareId || stateRef.current.currentEpisode?.content?.shareId;
    completionStateRef.current = {
      episodeId: episodeId || null,
      completed: false,
    };
    
    previewLimitActiveRef.current = false;
    if (playbackLockStackRef.current.length === 0) {
      setPlaybackLockState(prev => {
        if (prev.reason === FREE_PREVIEW_LOCK_REASON) {
          return { isLocked: false, reason: null };
        }
        return prev;
      });
    }
    
    
    // 检查是否为ID转换
    const isIdConversion = episodeId && currentEpisodeId && 
                          episodeId !== currentEpisodeId && 
                          checkIdConversion(episodeId, currentEpisodeId);
    
    // 只有当episode真正改变时才重置时间和使用initialTime（排除ID转换）
    const isNewEpisode = episodeId !== currentEpisodeId && !isIdConversion;

    // 仅当需要初始化 HTML5 音频播放器时才进入加载状态
    const origin = episode?.content?.origin || episode?.origin;
    const rawAudioUrl = episode?.audioUrl || episode?.content?.audioUrl || episode?.content?.videoUrl || episode?.videoUrl;
    const originalUrl = episode?.content?.videoUrl || episode?.videoUrl;
    const isYouTubeContent = typeof originalUrl === 'string' && (
      originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be')
    );
    const shouldAwaitAudioInit = Boolean(rawAudioUrl) && !(
      origin === 'video' ||
      isYouTubeContent ||
      isEpisodeBlobVideo(episode)
    );
    
    if (isIdConversion) {
      
      // ID转换：只更新episode对象，保持播放状态不变
      setState(prev => ({ 
        ...prev, 
        currentEpisode: episode,
        // 保持当前的播放状态、时间、加载状态
        duration: episode?.content?.duration || episode?.duration || prev.duration,
        // 确保播放器可见
        isVisible: true
      }));
      
      // 不重新初始化音频播放器，保持现有播放器
      return;
    }

    // 正常的episode变更处理
    if (isNewEpisode) {
      clearPlayerState();
      clearSubtitles();

      // 停止并清理现有的 HTML5 音频播放器，防止旧音频继续播放
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.removeEventListener('timeupdate', handleAudioTimeUpdate);
        audioPlayerRef.current.removeEventListener('ended', handleAudioEnded);
        audioPlayerRef.current.removeEventListener('loadedmetadata', handleAudioLoaded);
        audioPlayerRef.current = null;
        clearMediaSession();
      }
    }

    if (isNewEpisode) {
      setState(prev => ({
        ...prev,
        currentEpisode: episode,
        isVisible: !!episode,
        duration: episode?.content?.duration || episode?.duration || 0,
        currentTime: 0,
        isPlaying: false,
        isLoading: shouldAwaitAudioInit,
      }));

      if (initialTime && initialTime > 0) {
        const applyInitialTime = () => {
          setState(prev => ({
            ...prev,
            currentTime: initialTime,
          }));
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(applyInitialTime);
        } else {
          applyInitialTime();
        }
      }
    } else {
      setState(prev => ({
        ...prev,
        currentEpisode: episode,
        isVisible: !!episode,
        duration: episode?.content?.duration || episode?.duration || prev.duration,
        isLoading: prev.isLoading,
      }));
    }

    // 只有新episode才初始化音频播放器并传递initialTime
    if (isNewEpisode) {
      initializeAudioPlayer(episode, initialTime);
    } else {
      // 对于相同episode，确保已有音频播放器，如果没有则创建（但不传递initialTime）
      if (!audioPlayerRef.current) {
        initializeAudioPlayer(episode); // 不传递initialTime，保持当前状态
      }
    }
  }, [
    initializeAudioPlayer,
    checkIdConversion,
    clearPlayerState,
    isEpisodeBlobVideo,
    clearSubtitles,
    handleAudioTimeUpdate,
    handleAudioEnded,
    handleAudioLoaded,
    clearMediaSession,
  ]);

  // 使用 ref 来访问最新的 ytPlayer，避免依赖造成的重新创建
  const ytPlayerRef = useRef(state.ytPlayer);
  ytPlayerRef.current = state.ytPlayer;

  // 隐藏播放器容器引用（用于fallback YouTube播放器）
  const fallbackPlayerRef = useRef<HTMLDivElement | null>(null);
  
  // 🔥 优化：YouTube API预加载状态
  const youtubeAPIStatusRef = useRef<'unloaded' | 'loading' | 'ready'>('unloaded');
  const youtubeAPIPromiseRef = useRef<Promise<void> | null>(null);
  
  // 🔥 优化：播放器缓存，避免重复初始化相同视频
  const playerCacheRef = useRef<{
    videoId: string;
    player: any;
    lastUsed: number;
  } | null>(null);
  
  // 🔥 优化：预加载YouTube API
  const preloadYouTubeAPI = useCallback(() => {
    if (typeof window === 'undefined') return Promise.resolve();
    
    // 如果已经加载或正在加载，返回现有Promise
    if (youtubeAPIStatusRef.current === 'ready') {
      return Promise.resolve();
    }
    
    if (youtubeAPIStatusRef.current === 'loading') {
      return youtubeAPIPromiseRef.current || Promise.resolve();
    }
    
    // 开始加载API
    youtubeAPIStatusRef.current = 'loading';
    
    const apiPromise = new Promise<void>((resolve) => {
      if (window.YT && window.YT.Player) {
        youtubeAPIStatusRef.current = 'ready';
        resolve();
        return;
      }
      
      // 检查是否已有script标签
      const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.head.appendChild(script);
      }
      
      // 设置全局回调或轮询检查
      const checkAPI = () => {
        if (window.YT && window.YT.Player) {
          youtubeAPIStatusRef.current = 'ready';
          resolve();
        } else {
          setTimeout(checkAPI, 50); // 更频繁的检查
        }
      };
      
      // 如果全局回调存在，使用它；否则开始轮询
      if (typeof window.onYouTubeIframeAPIReady !== 'undefined') {
        const originalCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (originalCallback && typeof originalCallback === 'function') {
            originalCallback();
          }
          youtubeAPIStatusRef.current = 'ready';
          resolve();
        };
      } else {
        window.onYouTubeIframeAPIReady = () => {
          youtubeAPIStatusRef.current = 'ready';
          resolve();
        };
        checkAPI(); // 同时启动轮询作为后备
      }
    });
    
    youtubeAPIPromiseRef.current = apiPromise;
    return apiPromise;
  }, []);
  
  // 🔥 优化：预创建播放器容器
  const ensureFallbackContainer = useCallback(() => {
    if (!fallbackPlayerRef.current) {
      const container = document.createElement('div');
      container.id = 'fallback-youtube-player';
      container.style.cssText = `
        position: absolute !important;
        top: -9999px !important;
        left: -9999px !important;
        width: 1px !important;
        height: 1px !important;
        overflow: hidden !important;
        visibility: hidden !important;
        pointer-events: none !important;
      `;
      document.body.appendChild(container);
      fallbackPlayerRef.current = container;
    }
    return fallbackPlayerRef.current;
  }, []);

  // 🔥 优化：初始化fallback YouTube播放器 - 减少延迟
  const initializeFallbackYouTubePlayer = useCallback(async (episode: any) => {
    if (typeof window === 'undefined') return;
    
    const startTime = performance.now();
    
    try {
      // 提取YouTube视频ID
      // 🚀 优先使用blob URL（如果存在）
      const videoUrl = episode?.audioUrl || episode?.content?.audioUrl || episode?.content?.videoUrl || episode?.videoUrl;
      const videoId = videoUrl?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
      
      if (!videoId) {
        return;
      }

      // 🔥 优化：检查播放器缓存
      const cached = playerCacheRef.current;
      if (cached && cached.videoId === videoId && cached.player) {
        const cacheAge = Date.now() - cached.lastUsed;
        
        // 如果缓存未过期（5分钟内）且播放器仍然有效
        if (cacheAge < 5 * 60 * 1000) {
          try {
            // 验证播放器是否仍然可用
            if (typeof cached.player.getPlayerState === 'function') {
              // 更新使用时间
              cached.lastUsed = Date.now();
              
              // 设置到全局状态
              setState(prev => ({ ...prev, ytPlayer: cached.player }));
              ytPlayerRef.current = cached.player;
              
              // 设置当前时间
              if (stateRef.current.currentTime > 0) {
                cached.player.seekTo(stateRef.current.currentTime, true);
              }
              
              return;
            } else {
              playerCacheRef.current = null; // 清除无效缓存
            }
          } catch (error) {
            playerCacheRef.current = null; // 清除无效缓存
          }
        } else {
          playerCacheRef.current = null; // 清除过期缓存
        }
      }

      // 并行执行：API预加载和容器准备
      const [, container] = await Promise.all([
        preloadYouTubeAPI(),
        Promise.resolve(ensureFallbackContainer())
      ]);


      // 🔥 优化：使用更快的播放器配置
      const player = new window.YT.Player(container, {
        videoId: videoId,
        width: 1,
        height: 1,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3, // 关闭注释
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          playsinline: 1, // 内联播放
          origin: window.location.origin // 提供origin以加快初始化
        },
        events: {
          onReady: () => {
            // 立即设置到全局状态和ref
            setState(prev => ({ ...prev, ytPlayer: player }));
            ytPlayerRef.current = player;
            
            // 🔥 优化：缓存播放器以供后续使用
            playerCacheRef.current = {
              videoId: videoId,
              player: player,
              lastUsed: Date.now()
            };
            
            // 设置当前时间
            if (stateRef.current.currentTime > 0) {
              player.seekTo(stateRef.current.currentTime, true);
            }
          },
          onStateChange: (event: any) => {
            const isPlaying = event.data === window.YT.PlayerState.PLAYING;
            const isEnded = event.data === window.YT.PlayerState.ENDED;
            
            // 只在状态真正改变时更新
            if (stateRef.current.isPlaying !== isPlaying) {
              setState(prev => ({ ...prev, isPlaying }));
            }
            
            // 优化的时间更新机制
            if (isPlaying) {
              if (timeUpdateInterval.current) {
                clearInterval(timeUpdateInterval.current);
              }
              
              timeUpdateInterval.current = setInterval(() => {
                if (player && typeof player.getCurrentTime === 'function' && stateRef.current.isPlaying) {
                  const currentTime = player.getCurrentTime();
                  setState(prev => ({ ...prev, currentTime }));
                } else {
                  if (timeUpdateInterval.current) {
                    clearInterval(timeUpdateInterval.current);
                    timeUpdateInterval.current = null;
                  }
                }
              }, 500); // 优化：降低更新频率以减少 CPU 负载和发热
            } else {
              if (timeUpdateInterval.current) {
                clearInterval(timeUpdateInterval.current);
                timeUpdateInterval.current = null;
              }
            }

            if (isEnded) {
              const duration = typeof player.getDuration === 'function' ? player.getDuration() : stateRef.current.duration;
              const finishedAt = duration || stateRef.current.currentTime || 0;

              setState(prev => ({ ...prev, currentTime: finishedAt, isPlaying: false }));
              updateMediaSessionPlaybackState('paused');
              updateMediaSessionPositionState();

              if (finishedAt > 0) {
                markCompleted(finishedAt, duration);
              }
            }
          }
        }
      });

    } catch (error: any) {
      console.error('Fallback YouTube player initialization failed', error);
      // Fallback YouTube player initialization failed
    }
  }, [preloadYouTubeAPI, ensureFallbackContainer, markCompleted, updateMediaSessionPlaybackState, updateMediaSessionPositionState]);
  
  // 🔥 优化：在页面加载时预加载YouTube API
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // 延迟预加载，避免阻塞初始渲染
      const timer = setTimeout(() => {
        preloadYouTubeAPI().then(() => {
          // YouTube API pre-loaded successfully
        }).catch(() => {
          // YouTube API pre-load failed
        });
      }, 1000); // 1秒后开始预加载
      
      return () => clearTimeout(timer);
    }
  }, [preloadYouTubeAPI]);

  // 🔥 优化：播放控制 - 参考 Podverse 的简洁逻辑
  const play = useCallback(async () => {
    
    const currentEpisode = stateRef.current.currentEpisode;
    if (!currentEpisode) {
      pendingPlayRequestRef.current = false;
      return;
    }

    if (previewLimitActiveRef.current && (!isFreePlanUser || stateRef.current.currentTime < FREE_PREVIEW_MAX_SECONDS)) {
      previewLimitActiveRef.current = false;
      if (playbackLockStackRef.current.length === 0) {
        setPlaybackLockState(prev => {
          if (prev.reason === FREE_PREVIEW_LOCK_REASON) {
            return { isLocked: false, reason: null };
          }
          return prev;
        });
      }
    }

    if (isFreePlanUser) {
      const currentTime = stateRef.current.currentTime || 0;
      if (currentTime >= FREE_PREVIEW_MAX_SECONDS) {
        previewLimitActiveRef.current = true;
        if (playbackLockStackRef.current.length === 0) {
          setPlaybackLockState(prev => {
            if (prev.isLocked && prev.reason === FREE_PREVIEW_LOCK_REASON) {
              return prev;
            }
            return { isLocked: true, reason: FREE_PREVIEW_LOCK_REASON };
          });
        }
        pendingPlayRequestRef.current = false;
        setState(prev => ({ ...prev, isPlaying: false, currentTime: FREE_PREVIEW_MAX_SECONDS }));
        return;
      }
    }

    if (playbackLockStackRef.current.length > 0) {
      pendingPlayRequestRef.current = true;
      setState(prev => {
        if (!prev.isPlaying) {
          return prev;
        }
        return { ...prev, isPlaying: false };
      });
      return;
    }

    pendingPlayRequestRef.current = false;
    
    // 🔥 检测视频内容类型
    const origin = currentEpisode?.content?.origin || currentEpisode?.origin;
    const originalUrl = currentEpisode?.content?.videoUrl || currentEpisode?.videoUrl;
    const isYouTubeContent = originalUrl && (originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be'));
    const isBlobVideo = isEpisodeBlobVideo(currentEpisode);

    // 🔥 修复：检测YouTube视频ID模式，但只有当origin明确是video时才使用ID模式检测
    const videoId = currentEpisode?.content?.videoId || currentEpisode?.videoId;
    const isYouTubeVideoId = origin === 'video' && videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId); // 只有origin是video时才用ID检测

    const isOnEpisodePage = typeof window !== 'undefined' && window.location.pathname.startsWith('/episodes/');
    const isVideoLikeContent = origin === 'video' || isBlobVideo;
    const shouldUseEmbeddedVideo = isVideoLikeContent;

    // 🔥 修复双音频问题：对于视频内容，只更新状态，让视频播放器处理音频
    // 对于podcast内容，在episodes页面跳过，在podcast页面使用audio player
    const isPodcastOnEpisodePage = origin === 'podcast' && isOnEpisodePage;

    if (shouldUseEmbeddedVideo || isPodcastOnEpisodePage) {
      // console.log('🎬 Video content detected, delegating to video player:', { 
      //   origin, 
      //   isYouTubeContent, 
      //   isYouTubeVideoId, 
      //   videoId, 
      //   isBlobVideo, 
      //   isPodcastOnEpisodePage 
      // });

      // 如果之前存在 fallback YouTube 播放器，确保暂停
      if (ytPlayerRef.current?.pauseVideo) {
        ytPlayerRef.current.pauseVideo();
      }

      setState(prev => ({ ...prev, isPlaying: true }));
      
      // 确保音频播放器被暂停（避免冲突）
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }
      return;
    }
    
    // 🔥 YouTube播放器处理 - 简化逻辑（仅用于纯YouTube音频）
    if (isYouTubeContent && !isBlobVideo) {
      if (ytPlayerRef.current?.playVideo) {
        ytPlayerRef.current.playVideo();
        setState(prev => ({ ...prev, isPlaying: true }));
        
        // 防止音频冲突
        if (audioPlayerRef.current) {
          audioPlayerRef.current.pause();
        }
        return;
      } else {
        setState(prev => ({ ...prev, isLoading: true }));
        
        try {
          await initializeFallbackYouTubePlayer(currentEpisode);
          
          // 简化等待逻辑 - 最多等待2秒
          const startTime = Date.now();
          while (!ytPlayerRef.current?.playVideo && (Date.now() - startTime < 2000)) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          if (ytPlayerRef.current?.playVideo) {
            ytPlayerRef.current.playVideo();
            setState(prev => ({ ...prev, isPlaying: true, isLoading: false }));
            return;
          }
        } catch (error) {
          // Fallback player failed
        }
        
        setState(prev => ({ ...prev, isPlaying: false, isLoading: false }));
        return;
      }
    }
    
    // 🔥 HTML5音频播放器处理 - 确保音频正确加载后再播放
    // 重新检查是否为视频内容
    const canUseHtmlAudio = !shouldUseEmbeddedVideo && !isYouTubeContent;
    if (canUseHtmlAudio) {
      try {
        setState(prev => ({ ...prev, isLoading: true }));
        
        // 如果音频播放器不存在，先初始化它
        if (!audioPlayerRef.current) {
          initializeAudioPlayer(currentEpisode, stateRef.current.currentTime);
          
          // 等待音频播放器初始化
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (!audioPlayerRef.current) {
          setState(prev => ({ ...prev, isPlaying: false, isLoading: false }));
          return;
        }
        
        
        // 检查音频源是否设置
        if (!audioPlayerRef.current.src) {
          initializeAudioPlayer(currentEpisode, stateRef.current.currentTime);
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (!audioPlayerRef.current?.src) {
            setState(prev => ({ ...prev, isPlaying: false, isLoading: false }));
            return;
          }
        }
        
        // 等待音频元素变为可播放状态，并检查缓冲情况
        const audio = audioPlayerRef.current;
        const isVideoLikeForBuffering = origin === 'video' || isBlobVideo || Boolean(originalUrl);
        const requiredReadyState = isVideoLikeForBuffering ? 2 : 3; // 视频类内容无需等到 HAVE_FUTURE_DATA
        const requiredBufferedAhead = isVideoLikeForBuffering ? 1.5 : 10; // 视频只需短暂缓冲即可开播
        const bufferWaitTimeout = isVideoLikeForBuffering ? 2500 : 8000;
        
        // 检查缓冲状态
        const checkBuffering = () => {
          const buffered = audio.buffered;
          const currentTime = audio.currentTime;
          
          if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
              const start = buffered.start(i);
              const end = buffered.end(i);
              
              if (currentTime >= start && currentTime <= end) {
                const bufferAhead = end - currentTime;
                return bufferAhead;
              }
            }
          }
          return 0;
        };
        
        // 等待足够的缓冲或更高的readyState
        if (audio.readyState < requiredReadyState) {
          checkBuffering(); // 显示当前缓冲状态
          
          // 等待音频有足够缓冲
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              audio.removeEventListener('canplaythrough', onCanPlayThrough);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              resolve(void 0); // 超时后继续，不要完全阻塞
            }, bufferWaitTimeout);
            
            const onCanPlayThrough = () => {
              clearTimeout(timeout);
              audio.removeEventListener('canplaythrough', onCanPlayThrough);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              resolve(void 0);
            };
            
            const onCanPlay = () => {
              const bufferAhead = checkBuffering();
              
              if (bufferAhead >= requiredBufferedAhead) {
                clearTimeout(timeout);
                audio.removeEventListener('canplaythrough', onCanPlayThrough);
                audio.removeEventListener('canplay', onCanPlay);
                audio.removeEventListener('error', onError);
                resolve(void 0);
              }
            };
            
            const onError = (e: any) => {
              clearTimeout(timeout);
              audio.removeEventListener('canplaythrough', onCanPlayThrough);
              audio.removeEventListener('canplay', onCanPlay);
              audio.removeEventListener('error', onError);
              reject(new Error('Audio loading failed'));
            };
            
            audio.addEventListener('canplaythrough', onCanPlayThrough);
            audio.addEventListener('canplay', onCanPlay);
            audio.addEventListener('error', onError);
            
            // 如果已经有足够缓冲，立即检查
            if (audio.readyState >= requiredReadyState) {
              onCanPlay();
            }
          });
        }
        
        // 同步时间
        const stateTime = stateRef.current.currentTime;
        const audioTime = audio.currentTime;
        
        if (Math.abs(stateTime - audioTime) > 1 && stateTime > 0) {
          audio.currentTime = stateTime;
        }
        
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
        }
        
        setState(prev => ({ ...prev, isPlaying: true, isLoading: false }));
        updateMediaSessionMetadata();
        updateMediaSessionPlaybackState('playing');
        updateMediaSessionPositionState();
        
        // 防止YouTube播放器冲突
        if (ytPlayerRef.current?.pauseVideo) {
          ytPlayerRef.current.pauseVideo();
        }
        
        return;
        
      } catch (error) {
        
        setState(prev => ({ ...prev, isPlaying: false, isLoading: false }));
        return;
      }
    }
    
  }, [
    initializeFallbackYouTubePlayer,
    initializeAudioPlayer,
    isFreePlanUser,
    saveProgressImmediately,
    updateMediaSessionMetadata,
    updateMediaSessionPlaybackState,
    updateMediaSessionPositionState,
  ]);

  // 🔥 优化：暂停控制 - 参考 Podverse 的简洁逻辑
  const pause = useCallback(() => {
    pendingPlayRequestRef.current = false;
    
    const currentEpisode = stateRef.current.currentEpisode;
    if (!currentEpisode) {
      setState(prev => ({ ...prev, isPlaying: false }));
      return;
    }

    let skipHistoryOnThisPause = false;
    if (typeof window !== 'undefined') {
      const flag = (window as any).__PLAYER_SKIP_HISTORY_REPLAY__;
      const timeout = (window as any).__PLAYER_SKIP_HISTORY_REPLAY_TIMEOUT__;
      if (flag) {
        if (typeof timeout === 'number' && timeout < Date.now()) {
          delete (window as any).__PLAYER_SKIP_HISTORY_REPLAY__;
          delete (window as any).__PLAYER_SKIP_HISTORY_REPLAY_TIMEOUT__;
        } else {
          skipHistoryOnThisPause = true;
        }
      }
    }
    
    // 🔥 检测视频内容类型
    const origin = currentEpisode?.content?.origin || currentEpisode?.origin;
    const originalUrl = currentEpisode?.content?.videoUrl || currentEpisode?.videoUrl;
    const isYouTubeContent = originalUrl && (originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be'));
    const isBlobVideo = isEpisodeBlobVideo(currentEpisode);

    const isOnEpisodePage = typeof window !== 'undefined' && window.location.pathname.startsWith('/episodes/');
    const isVideoLikeContent = origin === 'video' || isBlobVideo;
    const shouldUseEmbeddedVideo = isVideoLikeContent;

    // 🔥 修复双音频问题：对于视频内容，只更新状态，让视频播放器处理暂停
    // 对于podcast内容，在episodes页面跳过，在podcast页面使用audio player
    const isPodcastOnEpisodePage = origin === 'podcast' && isOnEpisodePage;

    if (shouldUseEmbeddedVideo || isPodcastOnEpisodePage) {
      // console.log('🎬 Video content detected, delegating pause to video player:', { origin, isYouTubeContent, isBlobVideo, isPodcastOnEpisodePage });
      setState(prev => ({ ...prev, isPlaying: false }));

      // 确保音频播放器被暂停（如果存在）
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }

      updateMediaSessionPlaybackState('paused');
      updateMediaSessionPositionState();

      // 在全局播放器层面立即保存视频暂停进度，避免依赖子组件事件
      if (!skipHistoryOnThisPause && stateRef.current.currentTime > 0 && stateRef.current.duration) {
        console.log('💾 Context pause saving progress', {
          time: stateRef.current.currentTime,
          duration: stateRef.current.duration,
          reason: isBlobVideo ? 'blob_pause_context' : 'video_pause_context',
          episodeId: stateRef.current.currentEpisode?.shareId || stateRef.current.currentEpisode?.content?.shareId,
        });
        saveProgressImmediately(
          stateRef.current.currentTime,
          stateRef.current.duration,
          isBlobVideo ? 'blob_pause_context' : 'video_pause_context'
        );
      }
      return;
    }
    
    // YouTube播放器暂停（仅用于纯YouTube音频）
    if (isYouTubeContent && !isBlobVideo && ytPlayerRef.current?.pauseVideo) {
      ytPlayerRef.current.pauseVideo();
      setState(prev => ({ ...prev, isPlaying: false }));
      updateMediaSessionPlaybackState('paused');
      return;
    }

    // HTML5音频播放器暂停
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      
      const currentTime = audioPlayerRef.current.currentTime;
      setState(prev => ({ ...prev, isPlaying: false, currentTime }));
      
      updateMediaSessionPlaybackState('paused');
      updateMediaSessionPositionState();

      // 保存进度
      if (!skipHistoryOnThisPause && currentTime > 0 && audioPlayerRef.current.duration) {
        saveProgressImmediately(currentTime, audioPlayerRef.current.duration, 'pause');
      }
      return;
    }

    // 最后的后备方案
    setState(prev => ({ ...prev, isPlaying: false }));
    updateMediaSessionPlaybackState('paused');
  }, [saveProgressImmediately]);

  const releasePlaybackLockById = useCallback((id: symbol) => {
    const stack = playbackLockStackRef.current;
    const index = stack.findIndex(entry => entry.id === id);
    if (index === -1) {
      return;
    }

    const [entry] = stack.splice(index, 1);
    playbackLockStackRef.current = stack;

    if (stack.length > 0) {
      const next = stack[stack.length - 1];
      setPlaybackLockState({ isLocked: true, reason: next.reason });
      return;
    }

    setPlaybackLockState({ isLocked: false, reason: null });

    const shouldAutoResume = entry.autoResume && entry.wasPlaying;
    if (shouldAutoResume && !pendingPlayRequestRef.current) {
      pendingPlayRequestRef.current = true;
    }

    if (pendingPlayRequestRef.current) {
      setTimeout(() => {
        if (playbackLockStackRef.current.length === 0 && pendingPlayRequestRef.current) {
          play();
        }
      }, 0);
    }
  }, [play]);

  const acquirePlaybackLock = useCallback((reason: string, options?: PlaybackLockOptions) => {
    const entry: PlaybackLockEntry = {
      id: Symbol('playback-lock'),
      reason,
      autoResume: Boolean(options?.autoResume),
      wasPlaying: stateRef.current.isPlaying,
    };

    playbackLockStackRef.current = [...playbackLockStackRef.current, entry];
    setPlaybackLockState({ isLocked: true, reason: entry.reason });

    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      releasePlaybackLockById(entry.id);
    };
  }, [releasePlaybackLockById]);

  const seekTo = useCallback((time: number) => {
    const episodeForSeek = stateRef.current.currentEpisode;
    const episodeIdForSeek = episodeForSeek?.shareId || episodeForSeek?.content?.shareId;
    playerDebugLog('seekTo invoked', {
      requestedTime: time,
      currentTime: stateRef.current.currentTime,
      episodeId: episodeIdForSeek,
      lockDepth: playbackLockStackRef.current.length,
    });

    // 检查是否有 playback lock（如果有，阻止 seek 操作）
    if (playbackLockStackRef.current.length > 0) {
      playerDebugLog('seekTo blocked by playback lock', {
        requestedTime: time,
        reason: playbackLockStackRef.current[playbackLockStackRef.current.length - 1]?.reason,
      });
      console.log('⚠️ Seek blocked by playback lock:', playbackLockStackRef.current[playbackLockStackRef.current.length - 1]?.reason);
      return;
    }

    const isFreePlan = isFreePlanUserRef.current;
    if (isFreePlan && time > FREE_PREVIEW_MAX_SECONDS) {
      time = FREE_PREVIEW_MAX_SECONDS;
      previewLimitActiveRef.current = true;
      if (playbackLockStackRef.current.length === 0) {
        setPlaybackLockState(prev => {
          if (prev.isLocked && prev.reason === FREE_PREVIEW_LOCK_REASON) {
            return prev;
          }
          return { isLocked: true, reason: FREE_PREVIEW_LOCK_REASON };
        });
      }
    }

    if (isFreePlan && time < FREE_PREVIEW_MAX_SECONDS && previewLimitActiveRef.current) {
      previewLimitActiveRef.current = false;
      if (playbackLockStackRef.current.length === 0) {
        setPlaybackLockState(prev => {
          if (prev.reason === FREE_PREVIEW_LOCK_REASON) {
            return { isLocked: false, reason: null };
          }
          return prev;
        });
      }
    }

    const currentStateTime = stateRef.current.currentTime;
    const safePreviousTime = Number.isFinite(currentStateTime) ? currentStateTime : 0;
    const seekTimestamp = Date.now();
    lastSeekPreviousTimeRef.current = safePreviousTime;
    lastSeekTimeRef.current = time;
    lastSeekTimestampRef.current = seekTimestamp;
    pendingSeekRef.current = { target: time, startedAt: seekTimestamp };
    playerDebugLog('seekTo pending registered', {
      target: time,
      previousTime: safePreviousTime,
    });

    // 检查当前episode的类型
    const currentEpisode = stateRef.current.currentEpisode;
    const origin = currentEpisode?.content?.origin || currentEpisode?.origin;
    const originalUrl = currentEpisode?.content?.videoUrl || currentEpisode?.videoUrl;
    const isYouTubeContent = originalUrl && (originalUrl.includes('youtube.com') || originalUrl.includes('youtu.be'));
    const isBlobVideo = isEpisodeBlobVideo(currentEpisode);

    let seeked = false;

    const isOnEpisodePage = typeof window !== 'undefined' && window.location.pathname.startsWith('/episodes/');
    const isVideoLikeContent = origin === 'video' || isBlobVideo;
    const shouldUseEmbeddedVideo = isVideoLikeContent;

    // 🔥 修复：对于视频内容，不使用HTML5音频播放器进行seek
    // 对于podcast内容，在episodes页面跳过，在podcast页面使用audio player
    const isPodcastOnEpisodePage = origin === 'podcast' && isOnEpisodePage;

    // 对于视频内容或episodes页面的podcast，只更新状态，让VideoPlayer组件处理seek
    if (shouldUseEmbeddedVideo) {
      // console.log('🎬 Video content detected, updating state and delegating seek to embedded player:', { origin, isYouTubeContent, isBlobVideo });
      isSeekingRef.current = true;
      lastSeekTimeRef.current = time;
      lastSeekTimestampRef.current = Date.now();
      playerDebugLog('seekTo delegated to embedded player', {
        target: time,
        origin,
        isYouTubeContent,
        isBlobVideo,
        isPodcastOnEpisodePage,
      });
      setState(prev => ({ ...prev, currentTime: time }));
      updateActiveSubtitleByTime(time);
      setTimeout(() => {
        // 额外的状态同步，防止外部事件覆盖
        setState(prev => ({ ...prev, currentTime: time }));
        updateActiveSubtitleByTime(time);
      }, 150);
      setTimeout(() => {
        isSeekingRef.current = false;
      }, 1500);
      seeked = true;
    } else if (isPodcastOnEpisodePage) {
      // console.log('🎧 Podcast on episode page, applying seek to media elements');
      isSeekingRef.current = true;
      lastSeekTimeRef.current = time;
      lastSeekTimestampRef.current = Date.now();

      if (audioPlayerRef.current) {
        try {
          audioPlayerRef.current.currentTime = time;
        } catch (error) {
          playerDebugLog('seekTo podcast audio currentTime set failed', error);
        }
      }

      if (ytPlayerRef.current?.seekTo) {
        try {
          ytPlayerRef.current.seekTo(time, true);
        } catch (error) {
          playerDebugLog('seekTo podcast ytPlayer seek failed', error);
        }
      }

      setState(prev => ({ ...prev, currentTime: time }));
      updateActiveSubtitleByTime(time);
      updateMediaSessionPositionState();

      setTimeout(() => {
        setState(prev => ({ ...prev, currentTime: time }));
        updateActiveSubtitleByTime(time);
        updateMediaSessionPositionState();
      }, 150);

      setTimeout(() => {
        isSeekingRef.current = false;
      }, 500);

      return;
    }
    
    // 🔥 修复：对于YouTube视频，优先使用YouTube播放器
    if (isYouTubeContent && ytPlayerRef.current?.seekTo && typeof ytPlayerRef.current.seekTo === 'function') {
      try {
        // 设置seeking标志，防止时间更新冲突
        isSeekingRef.current = true;
        
        // 🔥 关键修复：直接调用YouTube API，绕过可能未就绪的组件包装器
        ytPlayerRef.current.seekTo(time, true); // true for allowSeekAhead
        
        // 记录seek操作
        lastSeekTimeRef.current = time;
        lastSeekTimestampRef.current = Date.now();
        playerDebugLog('seekTo executing via YouTube player', {
          target: time,
          origin,
        });
        
        // 🔥 关键修复：立即更新状态，确保UI同步
        setState(prev => ({ 
          ...prev, 
          currentTime: time
        }));
        updateActiveSubtitleByTime(time);
        
        // 🔥 新增：强制多次更新确保同步生效
        setTimeout(() => {
          setState(prev => ({ 
            ...prev, 
            currentTime: time  // 第一次强制更新
          }));
          updateActiveSubtitleByTime(time);
        }, 100);
        
        setTimeout(() => {
          setState(prev => ({ 
            ...prev, 
            currentTime: time  // 第二次强制更新，确保覆盖任何干扰
          }));
          updateActiveSubtitleByTime(time);
        }, 300);
        
        // 验证seek结果
        setTimeout(() => {
          if (ytPlayerRef.current?.getCurrentTime) {
            const actualTime = ytPlayerRef.current.getCurrentTime();
            
            // 如果时间不匹配，强制更新UI状态并重试
            if (Math.abs(time - actualTime) > 2) {
              // 强制更新UI到目标时间，不管YouTube播放器的实际时间
              setState(prev => ({ 
                ...prev, 
                currentTime: time
              }));
              updateActiveSubtitleByTime(time);
              
              // 继续尝试seek
              ytPlayerRef.current.seekTo(time, true);
              
              // 再次强制更新状态
              setTimeout(() => {
                setState(prev => ({ 
                  ...prev, 
                  currentTime: time
                }));
                updateActiveSubtitleByTime(time);
              }, 200);
            }
          }
        }, 500);
        
        // 清除seeking标志 - 延长时间确保seek操作完全完成
        setTimeout(() => {
          isSeekingRef.current = false;
          playerDebugLog('seekTo via YouTube finished', {
            target: time,
          });
        }, 3000); // 3秒后清除，给YouTube播放器足够时间完成多次重试
        
        seeked = true;
      } catch (error) {
        isSeekingRef.current = false;
        playerDebugLog('seekTo via YouTube error', {
          target: time,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
    
    // 对于音频内容，使用HTML5音频播放器
    if (!seeked && audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = time;
      setState(prev => ({ ...prev, currentTime: time }));
      updateActiveSubtitleByTime(time);
      updateMediaSessionPositionState();
      playerDebugLog('seekTo applied to HTML5 audio', {
        target: time,
      });
      
      seeked = true;
    }

    // 如果没有任何播放器实例，跳转到 episode 页面并设置时间
    if (!seeked) {
      const episodeId = stateRef.current.currentEpisode?.shareId || stateRef.current.currentEpisode?.content?.shareId;
      if (episodeId && !isOnEpisodePage) {
        playerDebugLog('seekTo fallback navigation', {
          target: time,
          episodeId,
        });
        window.location.href = `/episodes/${episodeId}?t=${Math.floor(time)}`;
      } else if (isOnEpisodePage) {
        setState(prev => ({ ...prev, currentTime: time }));
        updateActiveSubtitleByTime(time);
        updateMediaSessionPositionState();
        playerDebugLog('seekTo fallback state update on episode page', {
          target: time,
        });
      }
    }
  }, []);

  const scrubTo = useCallback((time: number) => {
    const current = stateRef.current.currentTime;
    if (Number.isFinite(current) && Math.abs(current - time) < 0.05) {
      return;
    }

    const episode = stateRef.current.currentEpisode;
    const origin = episode?.content?.origin || episode?.origin;
    const treatAsVideo = origin === 'video' || isEpisodeBlobVideo(episode);

    if (!treatAsVideo && audioPlayerRef.current) {
      try {
        audioPlayerRef.current.currentTime = time;
      } catch (error) {
        playerDebugLog('scrubTo audio currentTime set failed', error);
      }
    }

    if (ytPlayerRef.current?.seekTo) {
      try {
        ytPlayerRef.current.seekTo(time, true);
      } catch (error) {
        playerDebugLog('scrubTo ytPlayer seek failed', error);
      }
    }

    seekTo(time);

    if (scrubStateRef.current.resumeTimeout) {
      clearTimeout(scrubStateRef.current.resumeTimeout);
      scrubStateRef.current.resumeTimeout = null;
    }

    if (
      scrubStateRef.current.wasPlaying &&
      playbackLockStackRef.current.length === 0
    ) {
      if (!stateRef.current.isPlaying) {
        play();
      }

      try {
        if (treatAsVideo) {
          if (ytPlayerRef.current?.playVideo) {
            ytPlayerRef.current.playVideo();
          }
        } else if (audioPlayerRef.current && audioPlayerRef.current.paused) {
          audioPlayerRef.current.play().catch(() => {});
        }
      } catch (error) {
        playerDebugLog('scrubTo resume attempt failed', error);
      }

      scrubStateRef.current.resumeTimeout = setTimeout(() => {
        scrubStateRef.current.resumeTimeout = null;
        if (scrubStateRef.current.wasPlaying && !stateRef.current.isPlaying) {
          play();
          try {
            if (treatAsVideo && ytPlayerRef.current?.playVideo) {
              ytPlayerRef.current.playVideo();
            } else if (audioPlayerRef.current && audioPlayerRef.current.paused) {
              audioPlayerRef.current.play().catch(() => {});
            }
          } catch (error) {
            playerDebugLog('scrubTo delayed resume failed', error);
          }
        }
      }, 150);
    }
  }, [seekTo, play, isEpisodeBlobVideo]);

  const handleMediaEnded = useCallback(() => {
    const episode = stateRef.current.currentEpisode;
    if (!episode) {
      return;
    }
    const episodeId = episode.shareId || episode.content?.shareId;
    const duration = stateRef.current.duration;
    if (!episodeId || !duration || !Number.isFinite(duration)) {
      return;
    }

    completionStateRef.current = { episodeId, completed: true };
    setState(prev => ({ ...prev, isPlaying: false, currentTime: duration }));
    updateActiveSubtitleByTime(duration);
    markCompleted(duration, duration);
  }, [markCompleted, updateActiveSubtitleByTime]);

  // 跳转控制
  const skipBackward = useCallback(() => {
    const newTime = Math.max(0, stateRef.current.currentTime - 15);
    seekTo(newTime);
  }, [seekTo]);

  const skipForward = useCallback(() => {
    const newTime = Math.min(stateRef.current.duration, stateRef.current.currentTime + 15);
    seekTo(newTime);
  }, [seekTo]);

  useEffect(() => {
    playRef.current = play;
    pauseRef.current = pause;
    skipBackwardRef.current = skipBackward;
    skipForwardRef.current = skipForward;
    seekToRef.current = seekTo;
    configureMediaSessionActions();
  }, [play, pause, skipBackward, skipForward, seekTo, configureMediaSessionActions]);

  const setPlaybackSpeed = useCallback((speed: number) => {
    // 设置HTML5音频播放器的播放速度
    if (audioPlayerRef.current) {
      audioPlayerRef.current.playbackRate = speed;
    }
    
    // 设置YouTube播放器的播放速度
    if (ytPlayerRef.current?.setPlaybackRate) {
      ytPlayerRef.current.setPlaybackRate(speed);
    }
    
    setState(prev => ({ ...prev, playbackSpeed: speed }));
  }, []);

  // 状态更新 - 只有在没有HTML5音频播放器时才允许外部更新时间
  // 添加seeking标志，防止时间更新冲突
  const isSeekingRef = useRef(false);
  
  // 添加最后一次seek时间的跟踪
  const lastSeekTimeRef = useRef<number>(0);
  const lastSeekTimestampRef = useRef<number>(0);
  const lastSeekPreviousTimeRef = useRef<number | null>(null);
  
  const pendingSeekRef = useRef<{ target: number; startedAt: number } | null>(null);
  const isScrubbingRef = useRef(false);
  const scrubStateRef = useRef<{
    wasPlaying: boolean;
    resumeTimeout: ReturnType<typeof setTimeout> | null;
  }>({
    wasPlaying: false,
    resumeTimeout: null,
  });
  const completionStateRef = useRef<{ episodeId: string | null; completed: boolean }>({
    episodeId: null,
    completed: false,
  });

  const beginScrubbing = useCallback(() => {
    if (!isScrubbingRef.current) {
      playerDebugLog('beginScrubbing');
      scrubStateRef.current.wasPlaying = stateRef.current.isPlaying;
      if (scrubStateRef.current.resumeTimeout) {
        clearTimeout(scrubStateRef.current.resumeTimeout);
        scrubStateRef.current.resumeTimeout = null;
      }
      if (stateRef.current.isPlaying) {
        isScrubbingRef.current = true;
        pause();
        return;
      }
    }
    isScrubbingRef.current = true;
  }, [pause]);

  const endScrubbing = useCallback(() => {
    if (isScrubbingRef.current) {
      playerDebugLog('endScrubbing');
    }
    isScrubbingRef.current = false;
    if (scrubStateRef.current.resumeTimeout) {
      clearTimeout(scrubStateRef.current.resumeTimeout);
      scrubStateRef.current.resumeTimeout = null;
    }
    const wasPlaying = scrubStateRef.current.wasPlaying;
    scrubStateRef.current.wasPlaying = false;

    if (wasPlaying && playbackLockStackRef.current.length === 0) {
      const episode = stateRef.current.currentEpisode;
      const origin = episode?.content?.origin || episode?.origin;
      const treatAsVideo = origin === 'video' || isEpisodeBlobVideo(episode);

      const attemptResume = () => {
        if (!stateRef.current.isPlaying) {
          play();
        }
        try {
          if (treatAsVideo) {
            ytPlayerRef.current?.playVideo?.();
          } else if (audioPlayerRef.current?.paused) {
            audioPlayerRef.current.play().catch(() => {});
          }
        } catch (error) {
          playerDebugLog('endScrubbing resume attempt failed', error);
        }
      };

      attemptResume();

      const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
        scrubStateRef.current.resumeTimeout = null;
        if (!stateRef.current.isPlaying) {
          attemptResume();
        }
      }, 150);
      scrubStateRef.current.resumeTimeout = timeoutId;
    }
  }, [play, isEpisodeBlobVideo]);
  
  const updateTime = useCallback((time: number) => {
    playerDebugLog('updateTime received', {
      incoming: time,
      currentTime: stateRef.current.currentTime,
      pendingSeek: pendingSeekRef.current,
      isSeekingFlag: isSeekingRef.current,
    });
    // 🔥 修复：如果正在进行seek操作，忽略来自YouTube播放器的时间更新
    if (isSeekingRef.current) {
      const pendingSeek = pendingSeekRef.current;
      if (pendingSeek) {
        const distanceToTarget = Math.abs(time - pendingSeek.target);
        if (distanceToTarget <= SEEK_SETTLE_TOLERANCE) {
          playerDebugLog('updateTime seek satisfied while seeking', { time, pendingSeek });
          pendingSeekRef.current = null;
          isSeekingRef.current = false;
        } else {
          playerDebugLog('updateTime ignored: isSeekingRef active', { time });
          return;
        }
      } else {
        const distanceToLast = Math.abs(time - lastSeekTimeRef.current);
        if (Number.isFinite(distanceToLast) && distanceToLast <= SEEK_SETTLE_TOLERANCE) {
          playerDebugLog('updateTime last seek satisfied, clearing isSeekingRef', { time });
          isSeekingRef.current = false;
        } else {
          playerDebugLog('updateTime ignored: isSeekingRef active (no pending)', { time });
          return;
        }
      }
    }
    if (isScrubbingRef.current) {
      playerDebugLog('updateTime ignored: scrubbing active', { time });
      return;
    }

    const pendingSeek = pendingSeekRef.current;
    if (pendingSeek) {
      const distanceToTarget = Math.abs(time - pendingSeek.target);
      const timeSinceSeek = Date.now() - pendingSeek.startedAt;

      if (distanceToTarget <= SEEK_SETTLE_TOLERANCE) {
        playerDebugLog('updateTime pending seek satisfied', { time, pendingSeek });
        pendingSeekRef.current = null;
      } else if (timeSinceSeek < SEEK_GUARD_WINDOW_MS) {
        playerDebugLog('updateTime ignored: pending seek guard', {
          time,
          pendingSeek,
          distanceToTarget,
          timeSinceSeek,
        });
        return;
      } else {
        playerDebugLog('updateTime pending seek timed out', { time, pendingSeek, timeSinceSeek });
        pendingSeekRef.current = null;
      }
    }
    
    // 🔥 新增：如果这个时间更新试图覆盖最近的seek操作，阻止它
    const timeSinceLastSeek = Date.now() - lastSeekTimestampRef.current;
    const isRecentSeek = timeSinceLastSeek < 5000; // 5秒内
    if (isRecentSeek && lastSeekPreviousTimeRef.current !== null) {
      const targetTime = lastSeekTimeRef.current;
      const previousTime = lastSeekPreviousTimeRef.current;
      const distanceFromTarget = Math.abs(time - targetTime);
      const distanceFromPrevious = Math.abs(time - previousTime);
      const STALE_CALLBACK_TOLERANCE = 0.35;

      const isCloserToPreviousPosition = distanceFromPrevious + STALE_CALLBACK_TOLERANCE < distanceFromTarget;
      if (isCloserToPreviousPosition) {
        playerDebugLog('updateTime ignored: closer to previous position', {
          time,
          targetTime,
          previousTime,
        });
        return;
      }
    }
    
    // 🔥 修复podcast时间进度问题：检查是否为podcast在episodes页面
    const currentEpisode = stateRef.current.currentEpisode;
    const origin = currentEpisode?.content?.origin || currentEpisode?.origin;
    const treatAsVideo = origin === 'video' || isEpisodeBlobVideo(currentEpisode);
    const isPodcastOnEpisodePage = origin === 'podcast' && 
      (typeof window !== 'undefined' && window.location.pathname.startsWith('/episodes/'));
    
    // 如果有HTML5音频播放器，检查是否应该忽略外部更新
    if (audioPlayerRef.current && !isPodcastOnEpisodePage && !treatAsVideo) {
      const audioTime = audioPlayerRef.current.currentTime;
      
      // 如果音频正在播放，或者音频有有效时间且外部时间明显不同，忽略外部更新
      if (!audioPlayerRef.current.paused || (audioTime > 0.5 && Math.abs(time - audioTime) > 1)) {
        playerDebugLog('updateTime ignored: audio player authoritative', { time, audioTime });
        return;
      }
    }

    if (isFreePlanUser) {
      if (time >= FREE_PREVIEW_MAX_SECONDS) {
        const clampedTime = Math.min(time, FREE_PREVIEW_MAX_SECONDS);
        if (stateRef.current.currentTime !== clampedTime) {
          setState(prev => ({ ...prev, currentTime: clampedTime }));
          updateActiveSubtitleByTime(clampedTime);
        }

        const wasActivelyPlaying = stateRef.current.isPlaying || pendingPlayRequestRef.current;

        if (stateRef.current.isPlaying) {
          pause();
        }

        if (!previewLimitActiveRef.current && wasActivelyPlaying) {
          previewLimitActiveRef.current = true;
          if (playbackLockStackRef.current.length === 0) {
            setPlaybackLockState(prev => {
              if (prev.isLocked && prev.reason === FREE_PREVIEW_LOCK_REASON) {
                return prev;
              }
              return { isLocked: true, reason: FREE_PREVIEW_LOCK_REASON };
            });
          }
        }
        playerDebugLog('updateTime enforced free preview limit', { time, clampedTime });
        return;
      }

      if (previewLimitActiveRef.current && time < FREE_PREVIEW_MAX_SECONDS) {
        previewLimitActiveRef.current = false;
        if (playbackLockStackRef.current.length === 0) {
          setPlaybackLockState(prev => {
            if (prev.reason === FREE_PREVIEW_LOCK_REASON) {
              return { isLocked: false, reason: null };
            }
            return prev;
          });
        }
      }
    }
    
    const episodeId = stateRef.current.currentEpisode?.shareId || stateRef.current.currentEpisode?.content?.shareId;
    const duration = stateRef.current.duration;
    if (episodeId && duration) {
      const ratio = duration > 0 ? time / duration : 0;
      if (ratio >= 0.98) {
        if (!completionStateRef.current.completed || completionStateRef.current.episodeId !== episodeId) {
          completionStateRef.current = { episodeId, completed: true };
          markCompleted(duration, duration);
        }
      } else if (completionStateRef.current.episodeId === episodeId && completionStateRef.current.completed) {
        completionStateRef.current.completed = false;
      }
    }

    // Track progress for history (for YouTube videos)
    if (time > 0 && state.duration && user && state.currentEpisode) {
      trackProgress(time, state.duration);
    }
    
    playerDebugLog('updateTime applied', { time, origin });
    setState(prev => ({ ...prev, currentTime: time }));
    updateActiveSubtitleByTime(time);
  }, [isFreePlanUser, pause, trackProgress, updateActiveSubtitleByTime, isEpisodeBlobVideo, markCompleted]); // 移除user依赖，因为使用userRef

  const updateDuration = useCallback((duration: number) => {
    setState(prev => ({ ...prev, duration }));
  }, []);

  const setYtPlayer = useCallback((player: any) => {
    setState(prev => ({ ...prev, ytPlayer: player, isLoading: false }));
    // 立即更新 ref
    ytPlayerRef.current = player;
    
    // 🔥 修复：移除全局播放器的YouTube时间更新循环
    // YouTube播放器组件内部已经有自己的时间更新机制，不需要重复
    // 清除任何现有的时间更新循环，避免冲突
    if (timeUpdateInterval.current) {
      clearInterval(timeUpdateInterval.current);
      timeUpdateInterval.current = null;
    }
    
    // console.log('🎬 YouTube播放器已设置，移除全局时间更新循环以避免冲突');
  }, []);

  const setCleanReadData = useCallback((data: any) => {
    setState(prev => ({ ...prev, cleanReadData: data }));
  }, []);

  const setIsLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  // 标记是否为内部状态更新（不触发播放器控制）
  const isInternalStateUpdate = useRef(false);

  // 新增：仅更新播放状态，不触发播放器控制
  const updatePlayingState = useCallback((isPlaying: boolean) => {
    if (!isPlaying && (isScrubbingRef.current || isSeekingRef.current)) {
      playerDebugLog('updatePlayingState ignored during scrub/seek pause event');
      return;
    }

    // 当视频开始播放时，确保独占：暂停任何HTML5音频
    try {
      const episode = stateRef.current.currentEpisode;
      const origin = episode?.content?.origin || episode?.origin;
      const treatAsVideo = origin === 'video' || isEpisodeBlobVideo(episode);
      if (isPlaying && treatAsVideo && audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }
    } catch {}

    isInternalStateUpdate.current = true;
    setState(prev => ({ ...prev, isPlaying }));
    // 立即重置flag，因为setState是同步的更新flag
    setTimeout(() => {
      isInternalStateUpdate.current = false;
    }, 50);
  }, [isEpisodeBlobVideo]);

  // 显示/隐藏控制
  const showPlayer = useCallback(() => {
    setState(prev => ({ ...prev, isVisible: true }));
  }, []);

  const hidePlayer = useCallback(() => {
    setState(prev => ({ ...prev, isVisible: false }));
  }, []);

  // 清理定时器和音频播放器
  useEffect(() => {
    return () => {
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current);
      }
      
      // 清理音频播放器
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        // 不需要手动移除事件监听器，因为音频元素会被销毁
        audioPlayerRef.current = null;
        clearMediaSession();
      }
      
      // 清理fallback YouTube播放器
      if (fallbackPlayerRef.current) {
        document.body.removeChild(fallbackPlayerRef.current);
        fallbackPlayerRef.current = null;
      }
      
      // 🔥 优化：清理播放器缓存
      if (playerCacheRef.current?.player) {
        try {
          if (typeof playerCacheRef.current.player.destroy === 'function') {
            playerCacheRef.current.player.destroy();
          }
        } catch (error) {
          // Failed to destroy cached player
        }
        playerCacheRef.current = null;
      }
    };
  }, []); // 移除依赖，避免循环引用

  // 状态变化监听器 - 自动保存状态到 localStorage
  useEffect(() => {
    // 当关键状态变化时保存到 localStorage
    if (state.currentEpisode) {
      savePlayerState(state);
    } else {
      // 如果没有当前episode，清除保存的状态
      clearPlayerState();
    }
  }, [state.currentEpisode, state.currentTime, state.duration, state.playbackSpeed, savePlayerState, clearPlayerState]);

  // 页面卸载时保存状态
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (state.currentEpisode) {
        savePlayerState(state);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state, savePlayerState]);

  // 页面加载时的状态恢复
  useEffect(() => {
    const restorePlayerOnMount = () => {
      // 如果状态中有 currentEpisode 但没有音频播放器，重新初始化
      if (state.currentEpisode && !audioPlayerRef.current) {
        // console.log('🎵 Restoring audio player on mount');
        initializeAudioPlayer(state.currentEpisode, state.currentTime);
      }
    };
    
    // 延迟执行，确保组件完全挂载
    const timer = setTimeout(restorePlayerOnMount, 100);
    return () => clearTimeout(timer);
  }, [initializeAudioPlayer]); // 添加依赖，但只在挂载时执行

  useEffect(() => {
    if (!stateRef.current.currentEpisode) {
      return;
    }

    if (audioPlayerRef.current) {
      return;
    }

    initializeAudioPlayer(stateRef.current.currentEpisode, stateRef.current.currentTime);
  }, [pathname, initializeAudioPlayer, state.currentEpisode]);

  // URL 参数支持 - 检测时间参数
  // 使用ref跟踪上一个episode ID，只在episode改变时处理URL参数
  const lastProcessedEpisodeRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const currentEpisodeId = state.currentEpisode?.shareId || state.currentEpisode?.content?.shareId;
    
    // 只在有episode且episode改变时处理URL参数
    if (currentEpisodeId && currentEpisodeId !== lastProcessedEpisodeRef.current) {
      const urlParams = new URLSearchParams(window.location.search);
      const timeParam = urlParams.get('t');
      
      if (timeParam) {
        const urlTime = parseInt(timeParam, 10);
        const localStorageTime = state.currentTime || 0;
        
        if (!isNaN(urlTime)) {
          // 智能优先级处理：
          // 1. 如果localStorage中的时间很接近URL时间（差距<5秒），使用localStorage时间（更精确）
          // 2. 如果差距较大，说明是外部链接或有意的时间跳转，使用URL时间
          const timeDifference = Math.abs(urlTime - localStorageTime);
          const shouldUseUrlTime = timeDifference > 5 || localStorageTime === 0;
          
          if (shouldUseUrlTime) {
            setTimeout(() => {
              seekTo(urlTime);
            }, 500);
          }
          
          // 清理URL参数，避免刷新时重复处理
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
        }
      }
      
      lastProcessedEpisodeRef.current = currentEpisodeId;
    }
  }, [state.currentEpisode, seekTo, state.currentTime]);

  // 添加 setPlaybackRate 作为 setPlaybackSpeed 的别名
  const setPlaybackRate = useCallback((rate: number) => {
    setPlaybackSpeed(rate);
  }, [setPlaybackSpeed]);

  // 添加 getCurrentTime 方法
  const getCurrentTime = useCallback(() => {
    if (audioPlayerRef.current) {
      const audioTime = audioPlayerRef.current.currentTime;
      if (Number.isFinite(audioTime)) {
        return audioTime;
      }
    }

    if (ytPlayerRef.current?.getCurrentTime) {
      const videoTime = ytPlayerRef.current.getCurrentTime();
      if (Number.isFinite(videoTime)) {
        return videoTime;
      }
    }

    return stateRef.current.currentTime;
  }, []);

  const getPendingSeekInfo = useCallback(() => pendingSeekRef.current, []);

  // ==========================================
  // 🎯 拆分 Context Values（关键性能优化）
  // ==========================================

  /**
   * 🔥 实时状态值 - 频繁变化（每 500ms）
   * 只有少数组件应该订阅（时间显示、进度条）
   */
  const realtimeValue = useMemo<PlayerRealtimeState>(() => ({
    currentTime: state.currentTime,
    isPlaying: state.isPlaying,
    activeSubtitleIndex: state.activeSubtitleIndex,
  }), [
    state.currentTime,
    state.isPlaying,
    state.activeSubtitleIndex,
  ]);

  /**
   * 🔷 稳定状态值 - 很少变化
   * 大部分组件应该订阅这个
   */
  const stableValue = useMemo<PlayerStableState>(() => ({
    currentEpisode: state.currentEpisode,
    duration: state.duration,
    playbackSpeed: state.playbackSpeed,
    isLoading: state.isLoading,
    ytPlayer: state.ytPlayer,
    cleanReadData: state.cleanReadData,
    isVisible: state.isVisible,
    subtitles: state.subtitles,
    isFullPlayerOpen: state.isFullPlayerOpen,
  }), [
    state.currentEpisode,
    state.duration,
    state.playbackSpeed,
    state.isLoading,
    state.ytPlayer,
    state.cleanReadData,
    state.isVisible,
    state.subtitles,
    state.isFullPlayerOpen,
  ]);

  // 分离 state 和 actions 来避免循环依赖
  const actions = useMemo(() => ({
    setCurrentEpisode,
    play,
    pause,
    seekTo,
    scrubTo,
    setPlaybackSpeed,
    setPlaybackRate,
    getCurrentTime,
    getPendingSeekInfo,
    updateTime,
    updateDuration,
    setYtPlayer,
    setCleanReadData,
    setIsLoading,
    updatePlayingState,
    showPlayer,
    hidePlayer,
    skipBackward,
    skipForward,
    setSubtitles,
    clearSubtitles,
    setActiveSubtitleIndex,
    updateActiveSubtitleByTime,
    openFullPlayer,
    closeFullPlayer,
    toggleFullPlayer,
    beginScrubbing,
    endScrubbing,
    handleMediaEnded,
    formatTime,
  }), [
    setPlaybackRate,
    getCurrentTime,
    getPendingSeekInfo,
    setSubtitles,
    clearSubtitles,
    setActiveSubtitleIndex,
    updateActiveSubtitleByTime,
    openFullPlayer,
    closeFullPlayer,
    toggleFullPlayer,
    beginScrubbing,
    endScrubbing,
    scrubTo,
    handleMediaEnded,
  ]); // 这些函数都是用 useCallback 定义的，不会改变

  /**
   * ⚡ Actions 值 - 永远不变
   * 任何组件都可以安全订阅
   */
  const actionsValue = useMemo<PlayerActions & PlaybackLockAPI>(() => ({
    ...actions,
    acquirePlaybackLock,
    isPlaybackLocked: playbackLockState.isLocked,
    playbackLockReason: playbackLockState.reason,
  }), [actions, acquirePlaybackLock, playbackLockState.isLocked, playbackLockState.reason]);

  /**
   * 🔄 向后兼容：完整的 Context 值
   * 保留用于逐步迁移
   */
  const value = useMemo<PlayerContextValue>(() => ({
    ...state,
    ...actions,
    acquirePlaybackLock,
    isPlaybackLocked: playbackLockState.isLocked,
    playbackLockReason: playbackLockState.reason,
  }), [state, actions, acquirePlaybackLock, playbackLockState]);

  return (
    <PlayerActionsContext.Provider value={actionsValue}>
      <PlayerStableContext.Provider value={stableValue}>
        <PlayerRealtimeContext.Provider value={realtimeValue}>
          <PlayerContext.Provider value={value}>
            {children}
          </PlayerContext.Provider>
        </PlayerRealtimeContext.Provider>
      </PlayerStableContext.Provider>
    </PlayerActionsContext.Provider>
  );
};
